// RRMatchList — pool matches + optional final. Phase RR1.
// Admin: clicking a match opens the RRMatchDialog for result + schedule editing.
// Public: read-only.
import React, { useState } from 'react';
import { CalendarBlank, Clock, MapPin, Trophy, Plus, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { roundRobinAPI } from '../../lib/roundRobinApi';
import EntryDisplay from '../EntryDisplay';
import ConfirmDialog from '../events/ConfirmDialog';
import RRManualMatchEditor from './RRManualMatchEditor';
import { FormatAwareScoreFields } from '../scoring/FormatOfPlay';
import { localPartsToIso, sameInstant, isoToEventParts, fmtEventTime, fmtEventDate } from '../../lib/scheduleTime';

function StatusPill({ status, isFinal }) {
  const map = {
    pending:   'bg-[#f3f1ec] text-[#6a766e]',
    scheduled: isFinal ? 'bg-[#fffbf0] text-[#c9a93a] ring-1 ring-[#c9a93a]/30' : 'bg-amber-50 text-amber-800 ring-1 ring-amber-100',
    completed: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${map[status]}`}>{status === 'completed' ? 'Final' : status === 'scheduled' ? 'Ready' : 'Pending'}</span>;
}

function Chip({ icon: Icon, children, testId }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-[#6a766e]" data-testid={testId}>
      <Icon size={11} weight="duotone" className="text-[#013523]" /> {children}
    </span>
  );
}

function MatchCard({ m, onClick, canScore, poolLabel, finalTitle, onEditStructural, canEditStructural }) {
  const e1 = m.entry1, e2 = m.entry2;
  const w1 = m.winner_entry_id && e1 && m.winner_entry_id === e1.id;
  const w2 = m.winner_entry_id && e2 && m.winner_entry_id === e2.id;
  const clickable = canScore;
  const dateChip = m.scheduled_at ? fmtEventDate(m.scheduled_at) : null;
  const timeChip = m.scheduled_at ? fmtEventTime(m.scheduled_at) : null;
  const showAction = canScore && !m.is_final && e1 && e2;
  const teamRow = (e, won) => (
    <div className={`h-9 px-2.5 flex items-center rounded-lg border text-sm min-w-0 ${won ? 'bg-emerald-50 border-emerald-200 text-[#013523] font-semibold' : 'border-[#ece6dc] text-[#2a332c]'}`}>
      <div className="truncate w-full">{e ? <EntryDisplay entry={e} /> : <span className="italic text-[#9aa39d]">TBD</span>}</div>
    </div>
  );

  return (
    <div
      data-testid={`rr-match-${m.id}`}
      className={`h-full flex flex-col text-left border rounded-2xl p-3 bg-white shadow-sm transition-all
        ${m.is_final ? 'border-[#c9a93a]' : 'border-[#e3dcd1]'}
        ${clickable ? 'hover:border-[#013523] hover:shadow-md' : ''}`}>
      {/* TOP — match label / status */}
      <div className="h-6 flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[#9aa39d] font-medium flex items-center gap-1.5 min-w-0 truncate">
          {m.is_final ? <><Trophy size={10} weight="fill" className="text-[#c9a93a]" /> {finalTitle || 'Final'}</> : <>{poolLabel || `Pool ${m.pool_number}`} · Match {m.match_number}</>}
          {!m.is_final && m.round_number != null && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#f3f1ec] text-[#6a766e]" data-testid={`rr-match-${m.id}-round`}>R{m.round_number}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {canEditStructural && !m.is_final && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEditStructural?.(m); }}
              data-testid={`rr-match-${m.id}-edit-structural`}
              className="p-1 rounded text-[#6a766e] hover:text-[#013523]"
              title="Edit teams / round / court / time"
            ><PencilSimple size={12} /></button>
          )}
          <StatusPill status={m.status} isFinal={m.is_final} />
        </div>
      </div>
      {/* TEAMS — fixed-height rows so cards align */}
      <button
        type="button" onClick={() => clickable && onClick(m)}
        disabled={!clickable}
        className={`block w-full text-left ${clickable ? 'cursor-pointer' : 'cursor-default'}`}>
        {teamRow(e1, w1)}
        <div className="h-4 text-[9px] uppercase tracking-widest text-[#c1bcb1] text-center select-none leading-4">vs</div>
        {teamRow(e2, w2)}
      </button>
      {/* META — court / time (always reserved) */}
      <div className="h-6 mt-2 flex items-center gap-3">
        {dateChip && <Chip icon={CalendarBlank} testId={`rr-match-${m.id}-date`}>{dateChip}</Chip>}
        {timeChip && <Chip icon={Clock} testId={`rr-match-${m.id}-time`}>{timeChip}</Chip>}
        {m.court_label && <Chip icon={MapPin} testId={`rr-match-${m.id}-court`}>Court {m.court_label}</Chip>}
        {!dateChip && !timeChip && !m.court_label && <span className="text-[10px] text-[#c1bcb1]">Not scheduled</span>}
      </div>
      {/* RESULT + ACTION — pinned to the bottom */}
      <div className="mt-auto pt-2 border-t border-[#f0ebe1] h-11 flex items-center justify-between gap-2">
        <div className="text-[12px] text-[#015e3f] font-semibold tabular-nums truncate" data-testid={`rr-match-${m.id}-score`}>
          {m.score_text || <span className="text-[#c1bcb1] font-normal">No result</span>}
        </div>
        {showAction && (
          <button
            type="button"
            onClick={(ev) => { ev.stopPropagation(); onClick(m); }}
            data-testid={`rr-match-${m.id}-score-btn`}
            className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors ${
              m.status === 'completed'
                ? 'bg-[#f5f0e6] text-[#013523] border border-[#c9a93a] hover:bg-[#fffbf0]'
                : 'bg-[#013523] text-white hover:bg-[#025a3a]'
            }`}
          >
            {m.status === 'completed' ? 'Edit score' : 'Enter score'}
          </button>
        )}
      </div>
    </div>
  );
}

// Compact, content-driven per-pool championship card. Single title at the top
// (no duplication), TBD → finalists, then date/time/court + result. Natural
// flow height — no fixed heights, absolute positioning or negative margins.
function ChampionshipCard({ pc, match, onClick, canScore }) {
  const e1 = match.entry1, e2 = match.entry2;
  const w1 = match.winner_entry_id && e1 && match.winner_entry_id === e1.id;
  const w2 = match.winner_entry_id && e2 && match.winner_entry_id === e2.id;
  const clickable = canScore && e1 && e2;
  const dateChip = match.scheduled_at ? fmtEventDate(match.scheduled_at) : null;
  const timeChip = match.scheduled_at ? fmtEventTime(match.scheduled_at) : null;
  const teamRow = (e, won) => (
    <div className={`px-3 py-2 rounded-lg border text-sm break-words ${won ? 'bg-emerald-50 border-emerald-200 text-[#013523] font-semibold' : 'border-[#ece6dc] text-[#2a332c] bg-white/70'}`}>
      {e ? <EntryDisplay entry={e} /> : <span className="italic text-[#9aa39d]">TBD</span>}
    </div>
  );
  return (
    <div
      data-testid={`rr-championship-${pc.pool_number}`}
      className="border border-[#c9a93a] rounded-2xl bg-gradient-to-br from-[#fffbf0] to-[#fff7e0] p-4">
      {/* One primary title only */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-semibold text-[#013523] flex items-center gap-2 text-[15px] min-w-0">
          <Trophy size={16} weight="fill" className="text-[#c9a93a] shrink-0" />
          <span className="truncate">{pc.title}</span>
        </div>
        <StatusPill status={match.status} isFinal />
      </div>
      {/* Matchup */}
      <button
        type="button" onClick={() => clickable && onClick(match)} disabled={!clickable}
        className={`block w-full text-left ${clickable ? 'cursor-pointer' : 'cursor-default'}`}>
        {teamRow(e1, w1)}
        <div className="text-[9px] uppercase tracking-widest text-[#c1bcb1] text-center py-1 select-none">vs</div>
        {teamRow(e2, w2)}
      </button>
      {/* Date / time / court */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {dateChip && <Chip icon={CalendarBlank} testId={`rr-championship-${pc.pool_number}-date`}>{dateChip}</Chip>}
        {timeChip && <Chip icon={Clock} testId={`rr-championship-${pc.pool_number}-time`}>{timeChip}</Chip>}
        {match.court_label && <Chip icon={MapPin} testId={`rr-championship-${pc.pool_number}-court`}>Court {match.court_label}</Chip>}
        {!dateChip && !timeChip && !match.court_label && <span className="text-[10px] text-[#c1bcb1]">Not scheduled</span>}
      </div>
      {match.score_text && (
        <div className="mt-2 pt-2 border-t border-[#f0ebe1] text-[12px] text-[#015e3f] font-semibold tabular-nums">
          {match.score_text}
        </div>
      )}
    </div>
  );
}


function RRMatchDialog({ open, match, rr, rrId, onClose, onSaved }) {
  const [winnerId, setWinnerId] = useState(null);
  const [scoreText, setScoreText] = useState('');
  const [scoreData, setScoreData] = useState(null);
  const [scoreValid, setScoreValid] = useState(true);
  const [date, setDate] = useState(''); const [time, setTime] = useState(''); const [court, setCourt] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const structured = !!rr?.scoring_format;
  React.useEffect(() => {
    if (open && match) {
      setWinnerId(match.winner_entry_id || null);
      setScoreText(match.score_text || '');
      setScoreData(match.score_data || null);
      setScoreValid(true);
      if (match.scheduled_at) {
        const parts = isoToEventParts(match.scheduled_at);
        setDate(parts.date);
        setTime(parts.time);
      } else { setDate(''); setTime(''); }
      setCourt(match.court_label || '');
    }
  }, [open, match]);
  if (!open || !match) return null;
  const e1 = match.entry1, e2 = match.entry2;
  const canPickWinner = !!(e1 && e2);
  const newSched = localPartsToIso(date, time);
  const scheduleChanged = !sameInstant(newSched, match.scheduled_at) || (court.trim() || '') !== (match.court_label || '');
  const onScoreChange = ({ scoreText: st, winnerEntryId, valid, scoreData: sd }) => {
    setScoreText(st || '');
    setScoreData(sd || null);
    setWinnerId(winnerEntryId || null);
    setScoreValid(valid);
  };
  const resultReady = canPickWinner && !!winnerId && (!structured || scoreValid);

  const submit = async () => {
    setBusy(true);
    try {
      if (scheduleChanged) {
        await roundRobinAPI.updateSchedule(rrId, match.id, { scheduled_at: newSched, court_label: court.trim() || null });
      }
      if (resultReady) {
        await roundRobinAPI.recordResult(rrId, match.id, { winner_entry_id: winnerId, score_text: scoreText || null, score_data: structured ? scoreData : null });
        toast.success('Result saved.');
      } else if (scheduleChanged) { toast.success('Schedule saved.'); }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await roundRobinAPI.clearMatch(rrId, match.id); setConfirmClear(false); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Clear failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="rr-match-dialog">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-[#e3dcd1] sticky top-0 bg-white">
          <div className="text-sm font-medium text-[#013523]">{match.is_final ? 'Final' : `Pool ${match.pool_number} · Match ${match.match_number}`}</div>
        </div>
        <div className="p-4 space-y-4">
          {canPickWinner ? (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wider text-[#013523]">Result</div>
              {structured ? (
                <FormatAwareScoreFields
                  key={match.id}
                  event={rr}
                  entry1={e1}
                  entry2={e2}
                  initial={{ winner_entry_id: match.winner_entry_id, score_text: match.score_text, score_data: match.score_data }}
                  onChange={onScoreChange}
                />
              ) : (
                <>
                  {[e1, e2].map((e) => (
                    <button key={e.id} onClick={() => setWinnerId(e.id)}
                      data-testid={`rr-pick-winner-${e.id}`}
                      className={`block w-full text-left px-3 py-2 rounded-lg border transition-colors ${winnerId === e.id ? 'bg-[#013523] text-white border-[#013523]' : 'border-[#e3dcd1] text-[#013523]'}`}>
                      {e.display_name}
                    </button>
                  ))}
                  <label className="block text-xs text-[#6a766e]">Score (optional)
                    <input data-testid="rr-score-input" type="text" value={scoreText} onChange={(e) => setScoreText(e.target.value)} placeholder="e.g. 6-4, 6-3" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
                  </label>
                </>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-[#6a766e] bg-[#fbfaf8] border border-[#e3dcd1] rounded-lg px-3 py-2">
              Both opponents aren&apos;t set yet.
            </div>
          )}
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wider text-[#013523]">Schedule</div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] text-[#6a766e]">Date<input data-testid="rr-schedule-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" /></label>
              <label className="text-[11px] text-[#6a766e]">Time<input data-testid="rr-schedule-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" /></label>
            </div>
            <label className="text-[11px] text-[#6a766e]">Court<input data-testid="rr-schedule-court" type="text" value={court} onChange={(e) => setCourt(e.target.value)} placeholder="e.g. Court 2" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" /></label>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-[#e3dcd1] flex justify-between sticky bottom-0 bg-white">
          {canPickWinner ? <button data-testid="rr-clear-btn" onClick={() => setConfirmClear(true)} disabled={busy} className="px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs">Clear result</button> : <span/>}
          <div className="flex gap-2">
            <button data-testid="rr-cancel-btn" onClick={onClose} className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-xs text-[#013523]">Cancel</button>
            <button data-testid="rr-save-btn" onClick={submit} disabled={busy || (!resultReady && !scheduleChanged)} className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs disabled:opacity-60">
              {busy ? 'Saving…' : (resultReady ? 'Save result' : 'Save')}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title="Clear this result?"
        description="Winner and score will be reset. Standings and any downstream final will recompute."
        confirmLabel="Clear result"
        destructive
        busy={busy}
        onConfirm={clear}
      />
    </div>
  );
}

export default function RRMatchList({ rr, rrId, onChanged, admin, canScore }) {
  const canEnterScore = canScore ?? admin;
  const [active, setActive] = useState(null);
  const [structuralOpen, setStructuralOpen] = useState(false);
  const [structuralMatch, setStructuralMatch] = useState(null);
  const canEditStructural = admin && !['in_progress', 'completed', 'archived', 'cancelled'].includes(rr.status);
  const openStructural = (m) => { setStructuralMatch(m); setStructuralOpen(true); };
  const openNewMatch  = ()  => { setStructuralMatch(null); setStructuralOpen(true); };
  const labels = rr.pool_labels || {};
  const poolLabel = (pn) => labels[String(pn)] || `Pool ${pn}`;
  const byPool = {};
  rr.matches.filter((m) => !m.is_final).forEach((m) => {
    (byPool[m.pool_number] = byPool[m.pool_number] || []).push(m);
  });
  const final = rr.matches.find((m) => m.is_final);
  const finals = rr.matches.filter((m) => m.is_final);
  const fmt = rr.championship_format;
  return (
    <div className="space-y-4" data-testid="rr-matches">
      {canEditStructural && (
        <div className="flex justify-end">
          <button
            type="button" onClick={openNewMatch}
            data-testid="rr-new-match"
            className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs flex items-center gap-1"
          ><Plus size={12} weight="bold" /> New match</button>
        </div>
      )}
      {Object.keys(byPool).sort((a, b) => Number(a) - Number(b)).map((p) => (
        <div key={p} className="border border-[#e3dcd1] rounded-2xl bg-white p-4" data-testid={`rr-pool-${p}`}>
          <div className="font-medium text-[#013523] mb-3">{poolLabel(p)} matches</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {byPool[p].map((m) => (
              <MatchCard
                key={m.id} m={m} canScore={canEnterScore}
                onClick={setActive}
                poolLabel={poolLabel(m.pool_number)}
                onEditStructural={openStructural}
                canEditStructural={canEditStructural}
              />
            ))}
          </div>
        </div>
      ))}
      {Object.keys(byPool).length === 0 && canEditStructural && (
        <div className="border border-dashed border-[#e3dcd1] rounded-2xl bg-white/60 p-6 text-center text-[12px] text-[#6a766e]" data-testid="rr-empty-schedule">
          No matches scheduled yet. Use <span className="font-semibold text-[#013523]">New match</span> to build the schedule by hand,
          or pick a schedule mode above and click <span className="font-semibold text-[#013523]">Generate schedule</span>.
        </div>
      )}
      {fmt === 'per_pool' && finals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 items-start" data-testid="rr-championships">
          {(rr.pool_championships || []).map((pc) => {
            const fm = finals.find((m) => m.id === pc.final_match_id);
            if (!fm) return null;
            return (
              <ChampionshipCard
                key={pc.final_match_id}
                pc={pc}
                match={fm}
                onClick={setActive}
                canScore={canEnterScore}
              />
            );
          })}
        </div>
      )}
      {fmt !== 'per_pool' && fmt !== 'none' && final && (
        <div className="border border-[#c9a93a] rounded-2xl bg-gradient-to-br from-[#fffbf0] to-[#fff7e0] p-4" data-testid="rr-final-section">
          <div className="font-medium text-[#013523] mb-3 flex items-center gap-2"><Trophy size={14} weight="fill" className="text-[#c9a93a]" /> Final</div>
          <div className="max-w-md"><MatchCard m={final} canScore={canEnterScore} onClick={setActive} poolLabel={null} /></div>
        </div>
      )}
      <RRMatchDialog open={!!active} match={active} rr={rr} rrId={rrId} onClose={() => setActive(null)} onSaved={onChanged} />
      <RRManualMatchEditor
        rr={rr}
        open={structuralOpen}
        match={structuralMatch}
        onClose={() => setStructuralOpen(false)}
        onSaved={onChanged}
      />
    </div>
  );
}
